/**
 * The use-case description (ADR-0026 amendment 2026-10-17, §AU;
 * docs/architecture/use-case-description.md).
 *
 * One entry per use case. Every decision in code asks a PROPERTY of an entry
 * ("is this booked by the hour?", "is this a room?"), never the use case's
 * name — so a new use case such as `hall` is one entry here, not a new branch
 * in every place that once compared against "venue". `scripts/use-case-guard`
 * fails on a new name comparison outside these files.
 *
 * The property meanings are written once, in
 * `shared/use-cases/use-case-description.json`. This file is a typed COPY of
 * it, as is `frontend/admin-ui/src/utils/useCases.ts`. The three backends hold
 * no copy: they build from `shared/src/useCases.ts` (`@kolula/shared`,
 * docs/plans/shared-rules-package.md). Each copy's test, and each backend's,
 * compares it to the JSON, so they cannot drift; a change is made in the JSON,
 * the package and both frontend copies in one PR.
 *
 * The types have no defaults: a new property needs a value for every use case,
 * and the compiler asks for each one.
 *
 * `live` = the database knows the value. Everything here except
 * `describeAnyUseCase` answers for LIVE use cases only and treats the rest as
 * unknown, because Prisma rejects an enum value the database does not have:
 * `hall` went live with its enum migration (completion plan W1-1, PR #3397);
 * a use case added later starts with live = false and is invisible to every
 * reader until its migration is deployed.
 */

export type UseCase = "venue" | "apartment" | "hall" | "rental" | "storage" | "sup" | "keys" | "p2p" | "parcel";

export type Template = "space" | "stationUnit" | "handover";

/** What a booking of the SPACE template is like. */
export interface SpaceProperties {
  /** One booking unit: a time window on a day, or a stay of nights. */
  timeUnit: "hour" | "night";
  /** The room-family booking screens, mails and pricing rule it uses. */
  flow: "event" | "stay";
  /** A whole-day booking window: lead time and buffer in days, day-granular, full-day defaults (was FULL_DAY_USE_CASES). */
  windowInDays: boolean;
  /** The default follow-up buffer after a booking, in days, when none is configured. */
  followUpDefaultDays: number;
  /** A minimum number of nights applies. */
  minStay: boolean;
  /** Without a configured value, a booking is direct rather than a reservation request. */
  directBookingDefault: boolean;
  /** The booking asks for the event (a list, or free text). */
  eventTypes: boolean;
  /** The category's note is shown on the booking step. */
  categoryNote: boolean;
  /** The venue rate knobs (weekend mode, half day) apply. */
  rateUnits: boolean;
  /** The room may be divided into segments. */
  divisible: boolean;
  /** The allocations tab is offered before the first window, once the series season is set (W2-3, PO 2026-10-25). */
  allocationWindows: boolean;
}

export interface UseCaseDescription {
  live: boolean;
  /** A tenant may switch it on under Use cases. */
  enableable: boolean;
  template: Template;
  /** Set for the space template, `null` for every other. */
  space: Readonly<SpaceProperties> | null;
}

export const USE_CASE_DESCRIPTIONS: Readonly<Record<UseCase, Readonly<UseCaseDescription>>> = Object.freeze({
  venue: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    space: Object.freeze({
      timeUnit: "hour",
      flow: "event",
      windowInDays: true,
      followUpDefaultDays: 0,
      minStay: false,
      directBookingDefault: false,
      eventTypes: true,
      categoryNote: true,
      rateUnits: true,
      divisible: true,
      allocationWindows: false,
    }),
  }),
  apartment: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    space: Object.freeze({
      timeUnit: "night",
      flow: "stay",
      windowInDays: true,
      followUpDefaultDays: 1,
      minStay: true,
      directBookingDefault: false,
      eventTypes: false,
      categoryNote: false,
      rateUnits: false,
      divisible: false,
      allocationWindows: false,
    }),
  }),
  hall: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    space: Object.freeze({
      timeUnit: "hour",
      flow: "event",
      windowInDays: true,
      followUpDefaultDays: 0,
      minStay: false,
      directBookingDefault: false,
      eventTypes: true,
      categoryNote: true,
      rateUnits: true,
      divisible: true,
      allocationWindows: true,
    }),
  }),
  rental: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    space: null,
  }),
  storage: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    space: null,
  }),
  sup: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    space: null,
  }),
  keys: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    space: null,
  }),
  p2p: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    space: null,
  }),
  parcel: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    space: null,
  }),
});

/**
 * The entry for a stored or sent use case, case-insensitively, whether live
 * or not; `null` for anything unknown. Only for code that must know a
 * use case the database does not have yet (tests, the switch-on itself).
 */
export const describeAnyUseCase = (useCase: string | null | undefined): Readonly<UseCaseDescription> | null => {
  const key = String(useCase ?? "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(USE_CASE_DESCRIPTIONS, key)
    ? USE_CASE_DESCRIPTIONS[key as UseCase]
    : null;
};

/** The entry for a stored or sent LIVE use case, case-insensitively; `null` for anything else. */
export const describeUseCase = (useCase: string | null | undefined): Readonly<UseCaseDescription> | null => {
  const description = describeAnyUseCase(useCase);
  return description?.live ? description : null;
};

/** The space properties of a live use case, or `null` when it is not a space (or unknown). */
export const spaceOf = (useCase: string | null | undefined): Readonly<SpaceProperties> | null =>
  describeUseCase(useCase)?.space ?? null;

/** The room family: every live use case of the space template. */
export const isRoomFamily = (useCase: string | null | undefined): boolean =>
  describeUseCase(useCase)?.template === "space";

/** The LIVE use cases an entry matches, in description order — for lists and query filters. */
export const liveUseCasesWhere = (
  matches: (description: Readonly<UseCaseDescription>) => boolean,
): UseCase[] =>
  (Object.keys(USE_CASE_DESCRIPTIONS) as UseCase[]).filter(
    (useCase) => USE_CASE_DESCRIPTIONS[useCase].live && matches(USE_CASE_DESCRIPTIONS[useCase]),
  );
